import { DeliveryOrderDto, DispatchDeliveryOrderResponseDto, TransportationActivityDto } from '@/api/portal'
import { AppointmentConfirmed, DdoColumn } from '../types'
import { ColumnConfig } from './columns.types'
import { shortDdoStatusMapping, ddoStatusMapping, ddoStatusDarkerColorMapping } from './order-status.config'
import { loadTypeMapping } from './load-type.config'
import { doTypeMapping } from './do-type.config'
import { activityComboTypeMapping } from './activity.config'

export const ddoColumnsConfig: ColumnConfig<DispatchDeliveryOrderResponseDto>[] = [
    {
        key: DdoColumn.orderNumber,
        label: DdoColumn.orderNumber.toString(),
        csvLabel: DdoColumn.orderNumber.toString(),
        path: order => order?.number,
        minWidth: '185px',
        isNumeric: true,
        sortable: true,
    },
    {
        key: DdoColumn.doCreated,
        label: DdoColumn.doCreated.toString(),
        csvLabel: DdoColumn.doCreated.toString(),
        path: order => order?.deliveryOrder?.date,
        minWidth: '255px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.status,
        label: DdoColumn.status.toString(),
        csvLabel: DdoColumn.status.toString(),
        path: order => order?.status,
        minWidth: '240px',
        isMultiSelectGroup: true,
        mapping: ddoStatusMapping,
        options: [
            {
                title: 'New',
                expandable: true,
                color: ddoStatusDarkerColorMapping[DispatchDeliveryOrderResponseDto.status.NEW],
                options: [
                    DispatchDeliveryOrderResponseDto.status.NEW,
                    DispatchDeliveryOrderResponseDto.status.NEW_ACCEPTED,
                    DispatchDeliveryOrderResponseDto.status.NEW_REJECTED,
                ].map(key => ({
                    value: key,
                    label: shortDdoStatusMapping[key],
                    color: ddoStatusDarkerColorMapping[key],
                })),
            },
            {
                title: 'On Hold',
                expandable: true,
                color: ddoStatusDarkerColorMapping[DispatchDeliveryOrderResponseDto.status.HOLD_LOADED],
                options: [
                    DispatchDeliveryOrderResponseDto.status.HOLD_LOADED,
                    DispatchDeliveryOrderResponseDto.status.HOLD_ARRIVED,
                    DispatchDeliveryOrderResponseDto.status.HOLD_UNLOADED,
                    DispatchDeliveryOrderResponseDto.status.NOT_ON_FILE,
                ].map(key => ({
                    value: key,
                    label: shortDdoStatusMapping[key],
                    color: ddoStatusDarkerColorMapping[key],
                })),
            },
            {
                title: 'Released',
                expandable: true,
                color: ddoStatusDarkerColorMapping[DispatchDeliveryOrderResponseDto.status.RELEASED_LOADED],
                options: [
                    DispatchDeliveryOrderResponseDto.status.RELEASED_LOADED,
                    DispatchDeliveryOrderResponseDto.status.RELEASED_ARRIVED,
                    DispatchDeliveryOrderResponseDto.status.RELEASED_UNLOADED,
                    DispatchDeliveryOrderResponseDto.status.ON_FILE,
                ].map(key => ({
                    value: key,
                    label: shortDdoStatusMapping[key],
                    color: ddoStatusDarkerColorMapping[key],
                })),
            },
            {
                title: 'Ready for Dispatch',
                expandable: false,
                color: ddoStatusDarkerColorMapping[DispatchDeliveryOrderResponseDto.status.READY_FOR_DISPATCH],
                options: [DispatchDeliveryOrderResponseDto.status.READY_FOR_DISPATCH].map(key => ({
                    value: key,
                    label: shortDdoStatusMapping[key],
                    color: ddoStatusDarkerColorMapping[key],
                })),
            },
            {
                title: 'Dispatched',
                expandable: false,
                color: ddoStatusDarkerColorMapping[DispatchDeliveryOrderResponseDto.status.DISPATCHED_PLANNED],
                options: [
                    DispatchDeliveryOrderResponseDto.status.DISPATCHED_PLANNED,
                    DispatchDeliveryOrderResponseDto.status.DISPATCHED_ASSIGNED,
                    DispatchDeliveryOrderResponseDto.status.DISPATCHED_CONFIRMED,
                ].map(key => ({
                    value: key,
                    label: shortDdoStatusMapping[key],
                    color: ddoStatusDarkerColorMapping[key],
                })),
            },
            {
                title: 'In Progress',
                expandable: true,
                color: 'rgb(79, 79, 79)',
                options: [
                    DispatchDeliveryOrderResponseDto.status.IN_PROCESS_PICKUP,
                    DispatchDeliveryOrderResponseDto.status.COMPLETED_PICKUP,
                    DispatchDeliveryOrderResponseDto.status.IN_PROCESS_DELIVERY,
                    DispatchDeliveryOrderResponseDto.status.COMPLETED_DELIVERY,
                    DispatchDeliveryOrderResponseDto.status.IN_PROCESS_RETURN,
                ].map(key => ({
                    value: key,
                    label: shortDdoStatusMapping[key],
                    color: ddoStatusDarkerColorMapping[key],
                })),
            },
            {
                title: 'Completed',
                expandable: false,
                color: ddoStatusDarkerColorMapping[DispatchDeliveryOrderResponseDto.status.COMPLETED],
                options: [DispatchDeliveryOrderResponseDto.status.COMPLETED].map(key => ({
                    value: key,
                    label: shortDdoStatusMapping[key],
                    color: ddoStatusDarkerColorMapping[key],
                })),
            },
            {
                title: 'Cancelled',
                expandable: false,
                color: ddoStatusDarkerColorMapping[DispatchDeliveryOrderResponseDto.status.CANCELLED],
                options: [DispatchDeliveryOrderResponseDto.status.CANCELLED].map(key => ({
                    value: key,
                    label: shortDdoStatusMapping[key],
                    color: ddoStatusDarkerColorMapping[key],
                })),
            },
        ],
    },
    {
        key: DdoColumn.type,
        label: DdoColumn.type.toString(),
        csvLabel: DdoColumn.type.toString(),
        path: order => order?.deliveryOrder?.type,
        minWidth: '150px',
        mapping: doTypeMapping,
        popoverWidth: '150px',
        isMultiSelect: true,
        options: [
            {
                title: 'Load Types',
                options: [
                    DeliveryOrderDto.type.IMPORT,
                    DeliveryOrderDto.type.EXPORT,
                    DeliveryOrderDto.type.REPOSITION,
                ].map(type => ({
                    value: type,
                    label: doTypeMapping[type],
                })),
            },
        ],
    },
    {
        key: DdoColumn.containerType,
        label: 'Type',
        csvLabel: 'Container Type',
        path: order => order?.container?.containerType?.code,
        minWidth: '100px',
    },
    {
        key: DdoColumn.container,
        label: 'Number',
        csvLabel: 'Container Number',
        path: order => order?.container?.number,
        minWidth: '140px',
        isMultiText: true,
        popoverWidth: '700px',
    },
    {
        key: DdoColumn.ssl,
        label: DdoColumn.ssl.toString(),
        csvLabel: DdoColumn.ssl.toString(),
        path: order => order?.deliveryOrder?.steamShipLine?.name,
        minWidth: '140px',
        isMultiText: true,
        popoverWidth: '700px',
    },
    {
        key: DdoColumn.loadType,
        label: DdoColumn.loadType.toString(),
        csvLabel: 'Load Type',
        path: order => order?.loadType,
        minWidth: '150px',
        isMultiSelect: true,
        popoverWidth: '150px',
        mapping: loadTypeMapping,
        options: [
            {
                title: 'Load Types',
                options: [
                    DispatchDeliveryOrderResponseDto.loadType.LIVE_LOAD,
                    DispatchDeliveryOrderResponseDto.loadType.DROP_AND_PICK,
                ].map(type => ({
                    value: type,
                    label: loadTypeMapping[type],
                })),
            },
        ],
    },
    {
        key: DdoColumn.bookingBL,
        label: DdoColumn.bookingBL.toString(),
        csvLabel: DdoColumn.bookingBL.toString(),
        path: order =>
            order?.deliveryOrder?.type === DeliveryOrderDto.type.EXPORT ||
            order?.deliveryOrder?.type === DeliveryOrderDto.type.REPOSITION
                ? order?.deliveryOrder?.bookingNumber
                : order?.deliveryOrder?.billOfLadingNumber,
        minWidth: '180px',
        isMultiText: true,
        popoverWidth: '700px',
    },
    {
        key: DdoColumn.referenceNumber,
        label: DdoColumn.referenceNumber.toString(),
        csvLabel: DdoColumn.referenceNumber.toString(),
        path: order => order?.deliveryOrder?.cargo?.referenceNumber,
        minWidth: '180px',
        isMultiText: true,
        popoverWidth: '700px',
        sanitizePattern: /[^a-zA-Z0-9]/g, // Keep only letters and digits
    },
    {
        key: DdoColumn.poNumber,
        label: DdoColumn.poNumber.toString(),
        csvLabel: DdoColumn.poNumber.toString(),
        path: order => order?.deliveryOrder?.cargo?.purchaseOrderNumber,
        minWidth: '140px',
        isMultiText: true,
        popoverWidth: '700px',
    },
    {
        key: DdoColumn.chassis,
        label: DdoColumn.chassis.toString(),
        csvLabel: DdoColumn.chassis.toString(),
        path: order => order?.equipment?.chassisNumber,
        minWidth: '140px',
        isMultiText: true,
        popoverWidth: '700px',
    },
    {
        key: DdoColumn.pickupLocation,
        label: 'Location',
        csvLabel: 'Pickup Location',
        path: order => order?.pickupStage?.location?.name,
        minWidth: '235px',
        sortable: true,
    },
    {
        key: DdoColumn.pickupPlannedDate,
        label: 'Planned',
        csvLabel: 'Pickup Planned',
        path: order => order?.pickupStage?.plannedAppointmentDateTimeRange?.from,
        extraData: order => order?.pickupStage?.plannedAppointmentDateTimeRange?.to,
        minWidth: '305px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.deliveryLocation,
        label: 'Location',
        csvLabel: 'Delivery Location',
        path: order => order?.deliveryStage?.activity?.destination?.longName,
        extraData: order => order?.deliveryStage?.activity?.destination?.shortName,
        minWidth: '500px',
        sortable: true,
    },
    {
        key: DdoColumn.deliveryActivity,
        label: 'Activity',
        csvLabel: 'Delivery Activity',
        path: order => order?.deliveryStage?.activity?.comboType,
        mapping: activityComboTypeMapping,
        minWidth: '200px',
        isMultiSelect: true,
        options: [
            {
                title: 'Activity Types',
                options: [
                    // TransportationActivityDto.comboType.GOTO,
                    TransportationActivityDto.comboType.EMPTY,
                    TransportationActivityDto.comboType.OFF_DUTY,
                    // TransportationActivityDto.comboType.BOBTAIL_GOTO,
                    TransportationActivityDto.comboType.PICK_CHASSIS,
                    TransportationActivityDto.comboType.PICK_UP_FULL,
                    TransportationActivityDto.comboType.SCALE_FULL,
                    TransportationActivityDto.comboType.SCALE_TARE,
                    TransportationActivityDto.comboType.DROP_FULL_WITH_OUT_CHASSIS,
                    TransportationActivityDto.comboType.DROP_FULL_WITH_CHASSIS,
                    TransportationActivityDto.comboType.DROP_CHASSIS,
                    TransportationActivityDto.comboType.GET_UNLOADED,
                    TransportationActivityDto.comboType.GET_LOADED,
                    TransportationActivityDto.comboType.PICK_UP_EMPTY,
                    TransportationActivityDto.comboType.DROP_EMPTY_WITH_OUT_CHASSIS,
                    TransportationActivityDto.comboType.DROP_EMPTY_WITH_CHASSIS,
                ].map(type => ({
                    value: type,
                    label: activityComboTypeMapping[type],
                })),
            },
        ],
    },
    {
        key: DdoColumn.activityPlannedDate,
        label: DdoColumn.activityPlannedDate.toString(),
        csvLabel: DdoColumn.activityPlannedDate.toString(),
        path: order => order?.deliveryStage?.activity?.startPlannedDateTimeRange?.from,
        extraData: order => order?.deliveryStage?.activity?.startPlannedDateTimeRange?.to,
        minWidth: '305px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.activityCompletionDate,
        label: DdoColumn.activityCompletionDate.toString(),
        csvLabel: DdoColumn.activityCompletionDate.toString(),
        path: order => order?.deliveryStage?.activity?.completionActualDate,
        mapping: activityComboTypeMapping,
        minWidth: '295px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.appointmentPlannedDate,
        label: DdoColumn.appointmentPlannedDate.toString(),
        csvLabel: DdoColumn.appointmentPlannedDate.toString(),
        path: order =>
            order?.deliveryStage?.actualAppointmentDate || order?.deliveryStage?.plannedAppointmentDateTimeRange?.from,
        extraData: order =>
            order?.deliveryStage?.actualAppointmentDate
                ? undefined
                : order?.deliveryStage?.plannedAppointmentDateTimeRange?.to,
        textColor: order => {
            const isConfirmed = order?.deliveryStage?.plannedAppointmentDateTimeRange?.confirmed
            const isDelivered = Boolean(order?.deliveryStage?.actualAppointmentDate)
            return isDelivered ? undefined : isConfirmed === false ? 'rgb(234, 174, 118)' : undefined
        },
        minWidth: '305px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.appointmentConfirmed,
        label: DdoColumn.appointmentConfirmed.toString(),
        csvLabel: DdoColumn.appointmentConfirmed.toString(),
        path: order => {
            const isConfirmed = order?.deliveryStage?.plannedAppointmentDateTimeRange?.confirmed
            const isDelivered = Boolean(order?.deliveryStage?.actualAppointmentDate)

            if (typeof isConfirmed !== 'boolean') {
                return undefined
            }

            return isDelivered
                ? AppointmentConfirmed.Delivered
                : isConfirmed
                  ? AppointmentConfirmed.Confirmed
                  : AppointmentConfirmed.NotConfirmed
        },
        isMultiSelect: true,
        options: [
            {
                title: '',
                options: [
                    {
                        value: AppointmentConfirmed.Delivered,
                        label: AppointmentConfirmed.Delivered,
                    },
                    {
                        value: AppointmentConfirmed.Confirmed,
                        label: AppointmentConfirmed.Confirmed,
                    },
                    {
                        value: AppointmentConfirmed.NotConfirmed,
                        label: AppointmentConfirmed.NotConfirmed,
                    },
                ],
            },
        ],
        minWidth: '165px',
        popoverWidth: '165px',
    },
    {
        key: DdoColumn.returnLocation,
        label: 'Location',
        csvLabel: 'Return Location',
        path: order => order?.returnStage?.location?.name,
        minWidth: '235px',
        sortable: true,
    },
    {
        key: DdoColumn.returnPlannedDate,
        label: 'Planned',
        csvLabel: 'Return Planned',
        path: order => order?.returnStage?.plannedAppointmentDateTimeRange?.from,
        extraData: order => order?.returnStage?.plannedAppointmentDateTimeRange?.to,
        minWidth: '305px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.daysOut,
        label: DdoColumn.daysOut.toString(),
        csvLabel: DdoColumn.daysOut.toString(),
        path: order => order?.daysOut,
        minWidth: '155px',
        isNumeric: true,
        sortable: true,
    },
    {
        key: DdoColumn.firstReceivingDate,
        label: DdoColumn.firstReceivingDate.toString(),
        csvLabel: DdoColumn.firstReceivingDate.toString(),
        path: order => order?.deliveryOrder?.firstReceivingDate,
        minWidth: '265px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.cutoffDate,
        label: DdoColumn.cutoffDate.toString(),
        csvLabel: DdoColumn.cutoffDate.toString(),
        path: order => order?.deliveryOrder?.generalCutoffDate,
        minWidth: '265px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.lfdDate,
        label: DdoColumn.lfdDate.toString(),
        csvLabel: DdoColumn.lfdDate.toString(),
        path: order => order?.deliveryOrder?.lastFreeDateDemurrage,
        minWidth: '265px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.etaDate,
        label: DdoColumn.etaDate.toString(),
        csvLabel: DdoColumn.etaDate.toString(),
        path: order => order?.deliveryOrder?.vesselArrivalDate,
        minWidth: '265px',
        isDate: true,
        sortable: true,
    },
    {
        key: DdoColumn.documents,
        label: DdoColumn.documents.toString(),
        csvLabel: DdoColumn.documents.toString(),
        path: order => order?.id,
        extraData: order => order?.deliveryOrder?.cargo?.referenceNumber,
        minWidth: '235px',
        noFilter: true,
    },
]
